/**
 * Sentry Configuration for React Frontend
 *
 * Provides error tracking, performance monitoring, and session replay
 * for the AutoCount frontend application.
 */

import * as Sentry from "@sentry/react";
import React from "react";
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from "react-router-dom";

/**
 * Sentry configuration interface
 */
interface SentryConfig {
  dsn?: string;
  environment: string;
  release?: string;
  sampleRate: number;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  debug: boolean;
  enabled: boolean;
}

/**
 * Get Sentry configuration based on environment
 */
function getSentryConfig(): SentryConfig {
  const isDevelopment = process.env.NODE_ENV === "development";
  const isProduction = process.env.NODE_ENV === "production";

  return {
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment:
      process.env.REACT_APP_SENTRY_ENVIRONMENT ||
      process.env.NODE_ENV ||
      "development",
    release: process.env.REACT_APP_VERSION || "unknown",
    // Error sampling
    sampleRate: isDevelopment ? 1.0 : 0.1, // 100% in dev, 10% in prod
    // Performance monitoring
    tracesSampleRate: isDevelopment ? 1.0 : 0.1,
    // Session replay sampling
    replaysSessionSampleRate: isProduction ? 0.01 : 0, // 1% of sessions in prod
    replaysOnErrorSampleRate: isProduction ? 0.1 : 0, // 10% when errors occur
    // Debug mode
    debug: isDevelopment,
    // Whether Sentry is enabled
    enabled: Boolean(process.env.REACT_APP_SENTRY_DSN),
  };
}

/**
 * Initialize Sentry for React
 */
export function initializeSentry(): void {
  const config = getSentryConfig();

  // Skip initialization if not enabled
  if (!config.enabled) {
    console.log("Sentry monitoring is disabled (no DSN configured)");
    return;
  }

  try {
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,
      debug: config.debug,

      // Performance monitoring
      tracesSampleRate: config.tracesSampleRate,

      // Session replay
      replaysSessionSampleRate: config.replaysSessionSampleRate,
      replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,

      // Integrations
      integrations: [
        // Browser tracing for performance monitoring (if available)
        ...(Sentry.browserTracingIntegration
          ? [Sentry.browserTracingIntegration({})]
          : []),
        // Session replay (if available)
        ...(Sentry.replayIntegration
          ? [
              Sentry.replayIntegration({
                maskAllText: false,
                maskAllInputs: true,
              }),
            ]
          : []),
      ],

      // Data scrubbing
      beforeSend(event, hint) {
        // Filter out sensitive data from requests
        if (event.request) {
          // Remove sensitive headers
          if (event.request.headers) {
            delete event.request.headers["authorization"];
            delete event.request.headers["cookie"];
            delete event.request.headers["x-auth-token"];
          }

          // Remove sensitive data from request body
          if (event.request.data && typeof event.request.data === "object" && event.request.data !== null) {
            const data = event.request.data as Record<string, any>;
            const sensitiveFields = [
              "password",
              "token",
              "secret",
              "apiKey",
              "creditCard",
            ];
            sensitiveFields.forEach((field) => {
              if (field in data) {
                data[field] = "[REDACTED]";
              }
            });
          }
        }

        // Filter out specific errors we don't want to track
        const error = hint.originalException;
        if (error && typeof error === "object" && "message" in error) {
          const errorMessage = String(error.message);

          // Don't track network errors (user might be offline)
          if (errorMessage.includes("NetworkError")) {
            return null;
          }

          // Don't track canceled requests
          if (errorMessage.includes("AbortError")) {
            return null;
          }

          // Don't track ResizeObserver errors (benign browser issue)
          if (errorMessage.includes("ResizeObserver")) {
            return null;
          }

          // Don't track chunk load errors (usually ad blockers)
          if (errorMessage.includes("ChunkLoadError")) {
            return null;
          }
        }

        return event;
      },

      // Breadcrumb filtering
      beforeBreadcrumb(breadcrumb) {
        // Filter out noisy breadcrumbs
        if (
          breadcrumb.category === "console" &&
          breadcrumb.level === "debug"
        ) {
          return null;
        }

        // Don't track navigation to login (might contain tokens in URL)
        if (
          breadcrumb.category === "navigation" &&
          breadcrumb.data?.to?.includes("/login")
        ) {
          breadcrumb.data.to = "/login";
        }

        // Redact sensitive data in breadcrumbs
        if (breadcrumb.data) {
          const sensitiveKeys = ["password", "token", "secret"];
          sensitiveKeys.forEach((key) => {
            if (key in breadcrumb.data) {
              breadcrumb.data[key] = "[REDACTED]";
            }
          });
        }

        return breadcrumb;
      },

      // Ignore specific errors
      ignoreErrors: [
        // Browser extensions
        "top.GLOBALS",
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        // Network errors
        "NetworkError",
        "Failed to fetch",
        // Canceled operations
        "AbortError",
        "The operation was aborted",
        // Third-party scripts
        "Non-Error promise rejection captured",
        // React development warnings (should not appear in production)
        "Warning:",
      ],
    });

    console.log("Sentry monitoring initialized", {
      environment: config.environment,
      release: config.release,
      tracesSampleRate: config.tracesSampleRate,
    });
  } catch (error) {
    console.error("Failed to initialize Sentry:", error);
  }
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(user: {
  id?: string | number;
  username?: string;
  email?: string;
  storeId?: string | number;
  role?: string;
}): void {
  if (!getSentryConfig().enabled) return;

  Sentry.setUser({
    id: user.id?.toString(),
    username: user.username,
    email: user.email,
    // Custom attributes
    segment: {
      storeId: user.storeId?.toString(),
      role: user.role,
    },
  });
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser(): void {
  if (!getSentryConfig().enabled) return;
  Sentry.setUser(null);
}

/**
 * Add custom context to Sentry
 */
export function setSentryContext(
  key: string,
  context: Record<string, any>
): void {
  if (!getSentryConfig().enabled) return;
  Sentry.setContext(key, context);
}

/**
 * Add breadcrumb for tracking user actions
 */
export function addSentryBreadcrumb(
  message: string,
  category: string,
  level: Sentry.SeverityLevel = "info",
  data?: Record<string, any>
): void {
  if (!getSentryConfig().enabled) return;

  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Capture a message
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info"
): void {
  if (!getSentryConfig().enabled) return;
  Sentry.captureMessage(message, level);
}

/**
 * Capture an exception with optional context
 */
export function captureException(
  error: Error | string,
  context?: Record<string, any>
): void {
  if (!getSentryConfig().enabled) return;

  if (context) {
    Sentry.withScope((scope) => {
      scope.setContext("Additional Context", context);
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Create a transaction for performance monitoring
 */
export function startTransaction(
  name: string,
  op: string,
  description?: string
): any | null {
  if (!getSentryConfig().enabled) return null;

  // startTransaction is deprecated in Sentry v10+
  // Use startSpan instead if available
  if (typeof Sentry.startSpan === 'function') {
    return Sentry.startSpan({ name, op }, () => {});
  }

  return null;
}

/**
 * Sentry Profiler component for performance monitoring
 */
export const SentryProfiler = Sentry.withProfiler;

/**
 * Sentry Error Boundary component
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Hook for route tracking
 */
export const useSentryRouteTracking = (routeName: string) => {
  React.useEffect(() => {
    if (!getSentryConfig().enabled) return;

    // getCurrentHub is deprecated in Sentry v10+
    // Route tracking is now handled by browserTracingIntegration automatically
    Sentry.setTag('route', routeName);
  }, [routeName]);
};

// Export Sentry for direct access if needed
export { Sentry };